import { useEffect, useRef, useState } from "react";

export type PollingState<T> =
  | { status: "loading"; data: null; error: null }
  | { status: "ready"; data: T; error: null }
  | { status: "failed"; data: T | null; error: Error };

/** True only when a poll has failed before ever delivering data; a later failed tick keeps its last data. */
export function neverLoaded(q: PollingState<unknown>): boolean {
  return q.status === "failed" && q.data === null;
}

/**
 * Poll `fn` on an interval, or once when `intervalMs` is null.
 *
 * A failed tick keeps the last data and reports `status: "failed"` beside it,
 * so a page that has loaded once stays readable through a blip rather than
 * emptying itself. Polling pauses while the tab is hidden and runs immediately
 * on return, so a tab left open overnight does not spend the night refetching.
 */
export function usePolling<T>(fn: () => Promise<T>, intervalMs: number | null): PollingState<T> {
  const [state, setState] = useState<PollingState<T>>({ status: "loading", data: null, error: null });
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    /** One poll: fetch, report, and schedule the next one unless the tab is
     *  hidden or the interval was turned off. A failure keeps the last good
     *  data on screen rather than blanking the page. */
    async function tick() {
      try {
        const data = await fnRef.current();
        if (!cancelled) setState({ status: "ready", data, error: null });
      } catch (err) {
        if (!cancelled) setState((prev) => ({ status: "failed", data: prev.data, error: err as Error }));
      }
      if (!cancelled && intervalMs !== null && !document.hidden) {
        timer = setTimeout(tick, intervalMs);
      }
    }

    tick();
    /** Coming back to the tab ticks immediately instead of waiting out the interval. */
    const onVisibility = () => {
      if (!document.hidden && intervalMs !== null) tick();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);

  return state;
}
